import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Header,
  Link,
  SpaceBetween,
  StatusIndicator,
  Table,
  type TableProps,
} from '@cloudscape-design/components';
import { fetchAllRooms, setRoomTier, spawnTicket, type ChatRoom } from '../api/chat';

const HOMESERVER_URL =
  import.meta.env.VITE_CHAT_HOMESERVER_URL || 'https://chat-dev.onboarding.gdna.ai';

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ChatInbox() {
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyRoom, setBusyRoom] = useState<string | null>(null);

  useEffect(() => {
    void loadRooms();
  }, []);

  async function loadRooms() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllRooms();
      setRooms(data.rooms);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rooms');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleTier(room: ChatRoom) {
    setBusyRoom(room.room_id);
    setError(null);
    try {
      const next = room.tier === 'free' ? 'paid' : 'free';
      await setRoomTier(room.room_id, next);
      await loadRooms();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set tier');
    } finally {
      setBusyRoom(null);
    }
  }

  async function handleSpawnTicket(room: ChatRoom) {
    setBusyRoom(room.room_id);
    setError(null);
    try {
      await spawnTicket(room.room_id);
      await loadRooms();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to spawn ticket');
    } finally {
      setBusyRoom(null);
    }
  }

  const columns: TableProps.ColumnDefinition<ChatRoom>[] = [
    {
      id: 'tenant',
      header: 'Tenant',
      width: 160,
      cell: (item) => item.tenant_id,
    },
    {
      id: 'account',
      header: 'Account',
      width: 160,
      cell: (item) => item.account_id || '—',
    },
    {
      id: 'kind',
      header: 'Channel',
      width: 130,
      cell: (item) => <Badge>{item.kind}</Badge>,
    },
    {
      id: 'tier',
      header: 'Tier',
      width: 110,
      cell: (item) =>
        item.tier === 'paid' ? (
          <StatusIndicator type="success">Paid</StatusIndicator>
        ) : (
          <StatusIndicator type="info">Free</StatusIndicator>
        ),
    },
    {
      id: 'ticket',
      header: 'Linked ticket',
      width: 200,
      cell: (item) => item.linked_ticket_id || '—',
    },
    {
      id: 'created',
      header: 'Created',
      width: 150,
      cell: (item) => formatDate(item.created_at),
    },
    {
      id: 'actions',
      header: 'Actions',
      width: 260,
      cell: (item) => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="inline-link"
            loading={busyRoom === item.room_id}
            onClick={() => void handleToggleTier(item)}
          >
            {item.tier === 'free' ? 'Set paid' : 'Set free'}
          </Button>
          {!item.linked_ticket_id && (
            <Button
              variant="inline-link"
              loading={busyRoom === item.room_id}
              onClick={() => void handleSpawnTicket(item)}
            >
              Spawn ticket
            </Button>
          )}
        </SpaceBetween>
      ),
    },
  ];

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Unified inbox of customer support rooms across tenants on the Matrix chat host."
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button iconName="refresh" onClick={() => void loadRooms()}>
              Refresh
            </Button>
            <Link external href={HOMESERVER_URL}>
              Homeserver
            </Link>
          </SpaceBetween>
        }
      >
        Chat Inbox
      </Header>

      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Table
        columnDefinitions={columns}
        items={rooms}
        loading={loading}
        loadingText="Loading chat rooms..."
        trackBy="room_id"
        variant="full-page"
        stickyHeader
        stripedRows
        empty={
          <Box textAlign="center" padding="l">
            <Box variant="p" color="text-status-inactive">
              No chat rooms yet. Rooms appear here once customers start support conversations.
            </Box>
          </Box>
        }
        header={<Header counter={`(${rooms.length})`}>Support rooms</Header>}
      />
    </SpaceBetween>
  );
}
